"use client";
import React, { ReactNode, useCallback } from "react";
import { useMapSearchParams } from "modules/common/components/map/hooks/useMapSearchParams";
import useWotcLGS from "modules/common/components/map/hooks/useWotcLGS";
import YourStoresList from "modules/lgs/components/YourStoresList";
import SearchResultStoresList from "modules/lgs/components/SearchResultStoresList";
import SimpleStoreListSkeleton from "modules/lgs/components/SimpleStoreListSkeleton";
import { useSelectedStores } from "modules/lgs/stores/useLGSSettingsStore";
import type { LGS } from "modules/lgs/model/lgs";
import type { ShortLGS } from "modules/common/model/models";

function toShortLGS(store: LGS): ShortLGS {
  return {
    slug: store.slug,
    wotcId: store.wotcId,
    name: store.name,
    latitude: store.latitude,
    longitude: store.longitude,
    phoneNumber: store.phoneNumber,
    emailAddress: store.emailAddress,
    showEmailInSEL: store.showEmailInSEL,
    isTradingHub: store.isTradingHub,
  };
}

interface StoreSelectorGridProps {
  compact?: boolean;
  /** Save-state indicator shown next to the "Your stores" list title */
  saveIndicator?: ReactNode;
}

export function StoreSelectorGrid({
  compact = false,
  saveIndicator,
}: StoreSelectorGridProps) {
  const { selectedStores, toggleStoreSelection, isStoreSelected } =
    useSelectedStores();
  const { coords } = useMapSearchParams();
  const {
    isPlaceholderData,
    isLoading: isWotcLgsLoading,
    data: LGSsFromWotcQuery,
  } = useWotcLGS(coords);

  // isPlaceholderData, not isFetching: the previous coords' stores are the wrong
  // set to offer for selection, but a same-coords background refetch shouldn't
  // wipe the grid the user is checking boxes in.
  const isLoading = isWotcLgsLoading || isPlaceholderData;

  // Derive checked state from Zustand store (no local state needed)
  const shopCheckedState: Record<string, boolean> = {};
  if (LGSsFromWotcQuery) {
    for (const store of LGSsFromWotcQuery) {
      shopCheckedState[store.wotcId] = isStoreSelected(store.wotcId);
    }
    for (const store of selectedStores) {
      shopCheckedState[store.wotcId] = true;
    }
  }

  // Handle store selection/deselection via Zustand toggle
  const handleChange = useCallback(
    (storeId: number) => {
      // Try to find the store in WotC results first (for adding)
      const wotcStore = LGSsFromWotcQuery?.find((s) => s.wotcId === storeId);
      if (wotcStore) {
        toggleStoreSelection(toShortLGS(wotcStore));
        return;
      }

      // Fall back to selected stores (for removing)
      const selected = selectedStores.find((s) => s.wotcId === storeId);
      if (selected) {
        toggleStoreSelection(selected);
      }
    },
    [LGSsFromWotcQuery, selectedStores, toggleStoreSelection]
  );

  return (
    <div
      className="@container w-full"
      role="group"
      aria-label="Store selection"
    >
      <div
        className={
          compact
            ? "@md:grid-cols-2 @md:gap-x-6 grid grid-cols-1 gap-4"
            : "@md:grid-cols-2 grid grid-cols-1 gap-4 lg:gap-2"
        }
      >
        {/* Search Results Column */}
        <div className="w-full min-w-0">
          {isLoading || !LGSsFromWotcQuery ? (
            <SimpleStoreListSkeleton />
          ) : (
            <SearchResultStoresList
              LGSsFromWotcQuery={LGSsFromWotcQuery}
              handleChange={handleChange}
              shopCheckedState={shopCheckedState}
              selectionLimitReached={selectedStores.length >= 5}
              compact={compact}
            />
          )}
        </div>

        {/* Your Stores Column */}
        <div className="flex w-full min-w-0">
          {isLoading || !LGSsFromWotcQuery ? (
            <SimpleStoreListSkeleton />
          ) : (
            <YourStoresList
              userStores={selectedStores}
              handleChange={handleChange}
              shopCheckedState={shopCheckedState}
              compact={compact}
              saveIndicator={saveIndicator}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default StoreSelectorGrid;
